import {
    boolean,
    index,
    integer,
    jsonb,
    pgTable,
    text,
    timestamp,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import type { I18nJsonb } from "./i18n";

/** Shipping Zones (VTE / NEAR / FAR) */
export const shippingZone = pgTable("shipping_zone", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  code: text("code").notNull().unique(),
  name: jsonb("name").$type<I18nJsonb>().notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
    .notNull()
    .defaultNow(),
});

/** ผู้ให้บริการขนส่ง (master) — เลือกที่ที่อยู่ / snapshot บน order */
export const shippingCarrier = pgTable(
  "shipping_carrier",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    code: text("code").notNull().unique(),
    name: jsonb("name").$type<I18nJsonb>().notNull(),
    enabled: boolean("enabled").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("shipping_carrier_enabled").on(t.enabled),
    index("shipping_carrier_sort").on(t.sortOrder),
  ],
);

/** Shipping Rates (weight brackets per zone) */
export const shippingRate = pgTable(
  "shipping_rate",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    zoneId: text("zone_id")
      .notNull()
      .references(() => shippingZone.id, { onDelete: "cascade" }),
    weightMinGrams: integer("weight_min_grams").notNull(),
    weightMaxGrams: integer("weight_max_grams").notNull(),
    amount: integer("amount").notNull(),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("shipping_rate_zone_id").on(t.zoneId),
    index("shipping_rate_weight").on(
      t.zoneId,
      t.weightMinGrams,
      t.weightMaxGrams,
    ),
  ],
);

/** Province master (Geo) */
export const province = pgTable(
  "province",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    zoneId: text("zone_id").references(() => shippingZone.id, {
      onDelete: "set null",
    }),
    name: jsonb("name").$type<I18nJsonb>().notNull(),
    enabled: boolean("enabled").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("province_zone_id").on(t.zoneId),
    index("province_enabled").on(t.enabled),
  ],
);

/** District master (linked to province) */
export const district = pgTable(
  "district",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    provinceId: text("province_id")
      .notNull()
      .references(() => province.id, { onDelete: "cascade" }),
    name: jsonb("name").$type<I18nJsonb>().notNull(),
    enabled: boolean("enabled").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("district_province_id").on(t.provinceId),
    index("district_enabled").on(t.enabled),
  ],
);
